import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { CoinRow } from "./component/CoinRow/CoinRow";
import { styles } from "./stylesWatchlist";
import AppleSvg from "../../assets/svg/AppleSvg";
import CustomSelect from "@/components/UI/CustomSelect/CustomSelect";
import { useGetFocusListQuery } from "@/store/api/focusListApi";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import ArrowIcon from "@/assets/svg/ArrowIcon";
import type { FocusListItem, FocusListParams } from "@/store/api/focusListApi";
import { PREMIUM_TIME_OPTIONS, TIME_OPTIONS, TimeRangeValue } from "@/types/timeRanges";
import { useAppSelector } from "@/hooks/useAppSelector";
import { normalizeSentiment } from "@/helpers/normalizeSentiment";
import theme from "@/theme";

type Coin = {
  name: string;
  symbol: string;
  price: string;
  change: number;
  sentiment: number;
  likes: string;
  posts: number;
  focus: boolean;
  Icon: React.ElementType;
  percent: number;
};

const Watchlist = () => {
  const [sortField, setSortField] = useState<"post" | "likes" | "sentiment">("sentiment");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  // const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [allCoins, setAllCoins] = useState<Coin[]>([]);
  const loaderRef = useRef<HTMLTableRowElement | null>(null);
  const { subscriptionStatus } = useAppSelector((state) => state.auth);
  const hasPremium = subscriptionStatus === "ACTIVE";
  // const { data: sectorsData } = useGetSectorsQuery();
  // const { data: industriesData } = useGetIndustriesQuery();
  // const { data: categoriesData } = useGetCategoriesQuery();

  // const [assetType, setAssetType] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRangeValue | null>("24h");
  // const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  // const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // const industriesOptions = industriesData?.industries || [];
  // const sectorsOptions = sectorsData?.sectors || [];
  // const categoriesOptions = categoriesData?.categories || [];
  const [refreshKey, setRefreshKey] = useState(0);
  const sortFields: ("likes" | "post" | "sentiment")[] = ["likes", "post", "sentiment"];
  const navigationParams = useMemo(() => {
    return {
      timeRange,
      orderType: "Top",
    };
  }, [timeRange]);
  const queryParams: FocusListParams = useMemo(() => {
    const params: FocusListParams = {
      page,
      pageSize: 50,
      // sectors: selectedSectors,
      // industries: selectedIndustries,
      // categories: selectedCategories,
      // type: assetType.length === 1 ? assetType[0] : undefined,
      sortBy: sortField,
      order: sortDirection,
      _refresh: refreshKey,
    };

    if (timeRange) {
      params.time_range = timeRange;
    }
    return params;
  }, [
    page,
    sortField,
    sortDirection,
    // selectedSectors,
    // selectedIndustries,
    // selectedCategories,
    // assetType,
    timeRange,
    refreshKey,
  ]);

  const { data, isFetching } = useGetFocusListQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });
  const totalPages = data?.total ? Math.ceil(data.total / 50) : 1;

  useEffect(() => {
    setPage(1);
    setAllCoins([]);
  }, [
    // selectedIndustries,
    // selectedSectors,
    // selectedCategories,
    // assetType,
    timeRange,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    if (page === 1) {
      setAllCoins(transformApiData(data?.items || []));
    } else if (data?.items?.length) {
      const newCoins = transformApiData(data.items);
      setAllCoins((prev) => {
        const ids = new Set(prev.map((c) => c.symbol));
        const filteredNew = newCoins.filter((c) => !ids.has(c.symbol));
        return [...prev, ...filteredNew];
      });
    }
  }, [data?.items, page]);

  const isFetchingRef = useRef(isFetching);
  const pageRef = useRef(page);
  const totalPagesRef = useRef(totalPages);
  const allCoinsLengthRef = useRef(allCoins.length);
  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);
  useEffect(() => {
    totalPagesRef.current = totalPages;
  }, [totalPages]);
  useEffect(() => {
    allCoinsLengthRef.current = allCoins.length;
  }, [allCoins.length]);

  useEffect(() => {
    const currentRef = loaderRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          !isFetchingRef.current &&
          pageRef.current < totalPagesRef.current &&
          allCoinsLengthRef.current > 0
        ) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );
    if (currentRef) {
      observer.observe(currentRef);
    }
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  const handleSortClick = (field: "post" | "likes" | "sentiment") => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }

    setPage(1);
    setAllCoins([]);
  };

  const transformApiData = (data: FocusListItem[]): Coin[] => {
    return data.map((item) => {
      const rawChangeValue =
        typeof item.currentPrice === "number" && typeof item.previousPrice === "number"
          ? item.currentPrice - item.previousPrice
          : 0;

      const changePercent =
        typeof item.previousPrice === "number" && item.previousPrice !== 0
          ? Number(((rawChangeValue / item.previousPrice) * 100).toFixed(2))
          : 0;

      return {
        name: item.name || "N/A",
        symbol: item.symbol || "",
        price: typeof item.currentPrice === "number" ? `$${item.currentPrice.toFixed(2)}` : "$0.00",
        change: Number(rawChangeValue.toFixed(2)),
        percent: changePercent,
        sentiment: normalizeSentiment(item.sentiment) ?? 0,
        likes: item.totalLikes?.toLocaleString() ?? "0",
        posts: item.totalPosts ?? 0,
        focus: item.isInFocusList ?? false,
        Icon: AppleSvg,
      };
    });
  };
  const options = [...TIME_OPTIONS, ...PREMIUM_TIME_OPTIONS];

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h3" sx={styles.title}>
          Watchlist
        </Typography>

        <Box sx={styles.filtersWrapper}>
          {/* <CustomSelect
              options={industriesOptions}
              selected={selectedIndustries}
              onChange={(values) => {
                setSelectedIndustries(values);
                setRefreshKey((p) => p + 1);
              }}
              placeholder="Industries"
              checkbox
              multiple
              width="220px"
              renderValue={(selected) => {
                if (!selected.length) return "Industries";
                if (selected.length === industriesOptions.length) return "All";
                if (selected.length === 1) return selected[0];
                return `${selected.length} Selected`;
              }}
              disabled={!industriesOptions.length}
              loading={!industriesOptions.length}
            />

            <CustomSelect
              options={sectorsOptions}
              selected={selectedSectors}
              onChange={(values) => {
                setSelectedSectors(values);
                setRefreshKey((p) => p + 1);
              }}
              placeholder="Sectors"
              checkbox
              multiple
              width="220px"
              renderValue={(selected) => {
                if (!selected.length) return "Sectors";
                if (selected.length === sectorsOptions.length) return "All";
                if (selected.length === 1) return selected[0];
                return `${selected.length} Selected`;
              }}
              disabled={!sectorsOptions.length}
              loading={!sectorsOptions.length}
            />

            <CustomSelect
              options={categoriesOptions}
              selected={selectedCategories}
              onChange={(values) => {
                setSelectedCategories(values);
                setRefreshKey((p) => p + 1);
              }}
              placeholder="Categories"
              checkbox
              multiple
              width="220px"
              renderValue={(selected) => {
                if (!selected.length) return "Categories";
                if (selected.length === categoriesOptions.length) return "All";
                if (selected.length === 1) return selected[0];
                return `${selected.length} Selected`;
              }}
              disabled={!categoriesOptions.length}
              loading={!categoriesOptions.length}
            />

            <CustomSelect
              options={["ETF", "STOCK"]}
              selected={assetType}
              onChange={(values) => {
                setAssetType(values);
                setRefreshKey((p) => p + 1);
              }}
              placeholder="Asset Type"
              checkbox
              multiple
              width="220px"
              renderValue={(selected) => {
                if (!selected.length) return "Asset Type";
                if (selected.length === 3) return "All";
                if (selected.length === 1) return selected[0];
                return `${selected.length} Selected`;
              }}
            /> */}

          <CustomSelect
            options={options.map((opt) => opt.label)}
            selected={
              timeRange === null || timeRange === undefined
                ? []
                : [options.find((o) => o.value === timeRange)?.label || "All"]
            }
            onChange={(values) => {
              const label = values[0];
              const found = options.find((o) => o.label === label);
              setTimeRange(found ? found.value : null);
              setRefreshKey((p) => p + 1);
            }}
            placeholder="Time Range"
            multiple={false}
            width="160px"
            disabledOptions={!hasPremium ? PREMIUM_TIME_OPTIONS.map((o) => o.label) : []}
          />
        </Box>
      </Box>

      <TableContainer sx={styles.tableContainer} className="hide-scrollbar" component={Paper}>
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              <TableCell sx={{ ...styles.tableHeadCell, minWidth: "235px" }}>
                <Typography sx={styles.tableHeadText}>Name</Typography>
              </TableCell>
              <TableCell sx={styles.tableHeadCell}>
                <Typography sx={styles.tableHeadText}>Price</Typography>
              </TableCell>

              {sortFields.map((field) => {
                const isActive = sortField === field;
                return (
                  <TableCell key={field} sx={styles.tableHeadCell}>
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleSortClick(field)}
                    >
                      <Typography sx={styles.tableHeadText}>
                        {field === "post" ? "Posts" : field === "likes" ? "Likes" : "Sentiment"}
                      </Typography>
                      <ArrowIcon
                        size={16}
                        direction={isActive ? (sortDirection === "asc" ? "up" : "down") : "down"}
                        color={isActive ? theme.palette.custom.mainBlue : "#C4C4C4"}
                      />
                    </Box>
                  </TableCell>
                );
              })}
              <TableCell sx={styles.tableHeadCell}>
                <Typography sx={styles.tableHeadTextRemove}>Remove</Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={styles.tableBody}>
            {allCoins.length > 0 &&
              allCoins.map((coin) => (
                <CoinRow
                  key={coin.symbol}
                  coin={coin}
                  queryParams={navigationParams}
                />
              ))}

            <TableRow ref={loaderRef}>
              <TableCell
                colSpan={7}
                align="center"
                sx={{ paddingLeft: "24px", paddingRight: "24px" }}
              >
                {isFetching && <CustomLoader size={34} />}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Watchlist;
